import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import { queryKeys } from "@/lib/data/static";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createMatterInsuranceV3,
  deleteMatterInsuranceV1,
  updateMatterInsuranceV1,
} from "@/lib/data/requests";
import {
  type APIError,
  type APIResponse,
  type MatterV3,
  type MatterInsurance,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

const withMatterInsurances = (
  matter: MatterV3,
  insurances: MatterInsurance[],
): MatterV3 => ({
  ...matter,
  insurances,
});

export const useCreateMatterInsurance = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterInsuranceV3>[0]) =>
      createMatterInsuranceV3(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          const insurances = oldData.data.insurances;
          return {
            ...oldData,
            data: withMatterInsurances(oldData.data, [
              ...insurances,
              {
                ...data,
                connections: [],
                id: "optimistic",
              },
            ] as MatterInsurance[]),
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (showToast) {
        toast.error("Error creating matter insurance");
      }
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter insurance created successfully");
      }
    },
  });
};

export const useUpdateMatterInsurance = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterInsuranceV1>[0]) =>
      updateMatterInsuranceV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          const insurances = oldData.data.insurances;
          return {
            ...oldData,
            data: withMatterInsurances(
              oldData.data,
              insurances.map((insurance) =>
                insurance.id === data.id
                  ? { ...insurance, ...data }
                  : insurance,
              ),
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating matter insurance");
      }
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter insurance updated successfully");
      }
    },
  });
};

export const useDeleteMatterInsurance = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterInsuranceV1>[0]) =>
      deleteMatterInsuranceV1(data),
    onMutate: (data) => {
      const previousData = queryClient.getQueryData(
        matterDetailQueryKey(data.matterId),
      );

      // Optimistic update
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<MatterV3> | undefined) => {
          if (!oldData) return;
          const insurances = oldData.data.insurances;
          return {
            ...oldData,
            data: withMatterInsurances(
              oldData.data,
              insurances.filter((insurance) => insurance.id !== data.id),
            ),
          };
        },
      );

      return { previousData };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      if (showToast) {
        toast.error("Error deleting matter insurance");
      }
    },

    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter insurance deleted successfully");
      }
    },
  });
};
